import { useEffect, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { create, update } from "../../services/upload-files.service";
import { useDispatch, useSelector } from "react-redux";
import { addEmployee, start, updateEmployee } from "../../store/slices/TeamManagementSlice";
import useFetch from "../../hooks/useFetch";

const AddEmployee = ({ selectedRow, setShowOffcanvas, showOffcanvas, setSelectedRow, operation }) => {
    const [formData, setFormData] = useState();
    const [isChecked, setIsChecked] = useState(selectedRow.is_supervisor);
    const [errors, setErrors] = useState({});
    const empLoadMaster = useSelector((state) => state.teammanagement.data.mastereditemp);
    const { data } = useFetch('role');

    const userdetails = JSON.parse(localStorage.getItem('userdetails'));
    const dispatch = useDispatch();

    useEffect(() => {
        setIsChecked(selectedRow.is_supervisor);
    }, [selectedRow.is_supervisor]);

    // ✅ Handle Input Change
    const handleInputChange = (event) => {
        const { name, value, checked } = event.target;

        if (name === 'is_supervisor') {
            setIsChecked(checked);
            setSelectedRow((prev) => ({
                ...prev,
                is_supervisor: checked,
                supervisor_id: checked ? null : prev.supervisor_id,
            }));
        } else {
            setSelectedRow((prev) => ({
                ...prev,
                [name]: value,
            }));
        }

        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    // ✅ Form Validation
    const validateForm = () => {
        const newErrors = {};

        if (!selectedRow.name?.trim()) {
            newErrors.name = "Please Enter Employee Name";
        }

        if (!selectedRow.email?.trim()) {
            newErrors.email = "Please Enter an Email Address";
        } else if (!validateEmail(selectedRow.email)) {
            newErrors.email = "Please Enter a Valid Email Address";
        }

        if (!selectedRow.contact?.trim()) {
            newErrors.contact = "Please Enter a Mobile Number";
        } else if (!validateMobileNumber(selectedRow.contact)) {
            newErrors.contact = "Please Enter a Valid 10-digit Mobile Number";
        }

        if (!selectedRow.country_code?.trim()) {
            newErrors.country_code = "Please Select Country Code";
        }

        if (!selectedRow.department_id) {
            newErrors.department_id = "Please Select a Department";
        }

        if (!selectedRow.is_supervisor && !selectedRow.supervisor_id) {
            newErrors.supervisor_id = "Please Select a Supervisor";
        }

        if (!selectedRow.designation) {
            newErrors.designation = "Please Select a Designation";
        }

        if (!selectedRow.role_id) {
            newErrors.role_id = "Please Select a Role";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ✅ Submit Handler
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (validateForm()) {
            const fullContact = `${selectedRow.country_code}${selectedRow.contact}`;

            setFormData(
                operation === "update"
                    ? {
                        id: selectedRow.id,
                        name: selectedRow.name,
                        user_id: selectedRow.user_id,
                        email: selectedRow.email,
                        contact: fullContact,
                        is_supervisor: selectedRow.is_supervisor,
                        supervisor_id: selectedRow.is_supervisor === 1 ? null : selectedRow.supervisor_id,
                        department_id: selectedRow.department_id,
                        designation: selectedRow.designation,
                        role_id: selectedRow.role_id,
                        type: "NA",
                    }
                    : {
                        name: selectedRow.name,
                        email: selectedRow.email,
                        contact: fullContact,
                        is_supervisor: selectedRow.is_supervisor ? 1 : 0,
                        supervisor_id: selectedRow.supervisor_id,
                        department_id: selectedRow.department_id,
                        designation: selectedRow.designation,
                        role_id: selectedRow.role_id,
                        company_id: userdetails.company_id,
                        type: "NA",
                    }
            );

            setShowOffcanvas(false);
        }
    };

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validateMobileNumber = (num) => /^\d{10}$/.test(num);

    useEffect(() => {
        if (formData) {
            dispatch(start());
            if (operation === "create") {
                create(formData, "employee").then((res) => dispatch(addEmployee(res)));
            } else {
                update(formData, "employee").then((res) => dispatch(updateEmployee(res)));
            }
        }
    }, [formData, dispatch]);

    const resetForm = (e) => {
        e.preventDefault();
        setSelectedRow({
            id: "",
            name: "",
            user_id: "",
            email: "",
            contact: "",
            country_code: "+91",
            is_supervisor: false,
            supervisor_id: null,
            department_id: null,
            designation: null,
            role_id: null,
            status: "1"
        });
        setIsChecked(false);
        setErrors({});
    };

    const handleClose = () => {
        resetForm({ preventDefault: () => { } });
        setShowOffcanvas(false);
    };

    return (
        <Offcanvas show={showOffcanvas} onHide={handleClose} placement="end">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>{operation === "create" ? "Add" : "Edit"} Employee</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit}>
                    {/* Employee Name */}
                    <div className="mb-3">
                        <label className="form-label">Employee Name:</label>
                        <input
                            type="text"
                            className="form-control"
                            name="name"
                            value={selectedRow.name || ""}
                            onChange={handleInputChange}
                        />
                        {errors.name && <div className="text-danger small">{errors.name}</div>}
                    </div>

                    {/* Email */}
                    <div className="mb-3">
                        <label className="form-label">Email:</label>
                        <input
                            type="email"
                            className="form-control"
                            name="email"
                            value={selectedRow.email || ""}
                            onChange={handleInputChange}
                            disabled={operation !== "create"}
                        />
                        {errors.email && <div className="text-danger small">{errors.email}</div>}
                    </div>

                    {/* ✅ Contact + Country Code */}
                    <div className="mb-3">
                        <label className="form-label">Contact Number:</label>
                        <div className="d-flex gap-2">
                            <select
                                className="form-select w-25"
                                name="country_code"
                                value={selectedRow.country_code || "+91"}
                                onChange={handleInputChange}
                            >
                                <option value="+91">+91 </option>
                                <option value="+1">+1 </option>
                                <option value="+44">+44 </option>
                                <option value="+61">+61 </option>
                                <option value="+971">+971 </option>
                            </select>
                            <input
                                type="text"
                                className="form-control w-75"
                                name="contact"
                                placeholder="Enter 10-digit mobile number"
                                value={selectedRow.contact || ""}
                                onChange={handleInputChange}
                            />
                        </div>
                        {errors.country_code && <div className="text-danger small">{errors.country_code}</div>}
                        {errors.contact && <div className="text-danger small">{errors.contact}</div>}
                    </div>

                    {/* Department */}
                    <div className="mb-3">
                        <label className="form-label">Department:</label>
                        <select
                            className="form-select"
                            name="department_id"
                            value={selectedRow.department_id || ""}
                            onChange={handleInputChange}
                        >
                            <option value="">Select Department</option>
                            {empLoadMaster.departments?.length > 0 ? (
                                empLoadMaster.departments.map((dep) => (
                                    <option key={dep.id} value={dep.id}>
                                        {dep.department_name}
                                    </option>
                                ))
                            ) : (
                                <option disabled>No Department data available.</option>
                            )}
                        </select>
                        {errors.department_id && <div className="text-danger small">{errors.department_id}</div>}
                    </div>

                    {/* Is Supervisor */}
                    <div className="form-check mb-3">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="is_supervisor"
                            name="is_supervisor"
                            checked={isChecked}
                            onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="is_supervisor">
                            Is Supervisor?
                        </label>
                    </div>

                    {/* Supervisor */}
                    {!isChecked && (
                        <div className="mb-3">
                            <label className="form-label">Supervisor:</label>
                            <select
                                className="form-select"
                                name="supervisor_id"
                                value={selectedRow.supervisor_id || ""}
                                onChange={handleInputChange}
                            >
                                <option value="">Select Supervisor</option>
                                {empLoadMaster.supervisors?.length > 0 ? (
                                    empLoadMaster.supervisors.map((sup) => (
                                        <option key={sup.id} value={sup.id}>
                                            {sup.name}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No Supervisor data available.</option>
                                )}
                            </select>
                            {errors.supervisor_id && <div className="text-danger small">{errors.supervisor_id}</div>}
                        </div>
                    )}

                    {/* Designation */}
                    <div className="mb-3">
                        <label className="form-label">Designation:</label>
                        <select
                            className="form-select"
                            name="designation"
                            value={selectedRow.designation || ""}
                            onChange={handleInputChange}
                        >
                            <option value="">Select Designation</option>
                            {empLoadMaster.designations?.length > 0 ? (
                                empLoadMaster.designations.map((des) => (
                                    <option key={des.id} value={des.id}>
                                        {des.name}
                                    </option>
                                ))
                            ) : (
                                <option disabled>No Designation data available.</option>
                            )}
                        </select>
                        {errors.designation && <div className="text-danger small">{errors.designation}</div>}
                    </div>

                    {/* Role */}
                    <div className="mb-3">
                        <label className="form-label">Select Role:</label>
                        <select
                            className="form-select"
                            name="role_id"
                            value={selectedRow.role_id || ""}
                            onChange={handleInputChange}
                        >
                            <option value="">Select Role</option>
                            {data.data?.length > 0 ? (
                                data.data.map((role) => (
                                    <option key={role.id} value={role.id}>
                                        {role.role_name}
                                    </option>
                                ))
                            ) : (
                                <option disabled>No Roles available.</option>
                            )}
                        </select>
                        {errors.role_id && <div className="text-danger small">{errors.role_id}</div>}
                    </div>

                    {/* Status */}
                    <div className="mb-3">
                        <label className="form-label">User Status:</label>
                        <select
                            className="form-select"
                            name="status"
                            value={selectedRow.status || "1"}
                            onChange={handleInputChange}
                            disabled={operation === "create"}
                        >
                            <option value="1">Active</option>
                            <option value="2">Inactive</option>
                        </select>
                    </div>

                    {/* Buttons */}
                    <div className="d-flex gap-2">
                        <button type="submit" className="btn btn-primary btn-add">
                            {operation === "create" ? "Add" : "Update"}
                        </button>
                        <button type="reset" className="btn btn-secondary" onClick={resetForm}>
                            Reset
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default AddEmployee;
